import type { DocumentTrainingEventUserRecord } from '@pbk-web/main-training';
import type { FormType } from '@pbk-web/types';
import type { AxiosRequestConfig } from 'axios';

import type { Access } from '../models/Access';
import type { ApiResponse } from '../models/ApiResponse';
import type { AssociationListItem, TemplateAssociation } from '../models/Association';
import type {
  ActivityPagedFilter,
  Attachment,
  ClientForm,
  ClientFormMetaDataResponse,
  ClientFormProgressResponse,
  ClientFormRequest,
  ClientFormSectionInfo,
  FormReferenceDirection,
  SignatureDetails,
  SignResponse,
} from '../models/ClientForm';
import type { ClientFormSectionStatus } from '../models/ClientFormSectionStatus';
import type { ClientFormShare } from '../models/ClientFormShare';
import type {
  ClientFormUser,
  ClientFormUserDto,
  FormAccessResponse,
  FormAccessRoleGroupRequest,
  FormAccessSnapshotResponse,
} from '../models/ClientFormUser';
import type { ClientFormUserStats } from '../models/ClientFormUserStats';
import type ClientFormVersionHistory from '../models/ClientFormVersionHistory';
import type { VersionState } from '../models/ClientFormVersionHistory';
import type { DocumentListItem, DocumentResponse } from '../models/Document';
import type { FormListItem } from '../models/Form';
import type User from '../models/User';
import BaseService from './BaseService';

export default class ClientFormService extends BaseService {
  public static getFormsPaged(
    clientId: string,
    filter: ActivityPagedFilter = {},
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<FormListItem[] | DocumentListItem[]>> {
    return this.post('/v1/forms/paged', { ClientId: clientId, ...filter }, config);
  }

  public static allocateToClient(payload: ClientFormRequest): Promise<ApiResponse<ClientForm>> {
    return this.post('/v1/forms', payload, { ignoredErrorStatusCodes: [403] });
  }

  public static allocateToTrainings(formId: string, trainingIds: string[]): Promise<ApiResponse<boolean>> {
    return this.post(`/v1/forms/${formId}/training`, trainingIds);
  }

  public static allocateToClientBulk(payload: ClientFormRequest[]): Promise<ApiResponse<ClientForm[]>> {
    return this.post('/v1/forms/bulk', payload);
  }

  public static getForms(clientId: string, filter: ActivityPagedFilter = {}): Promise<ApiResponse<FormListItem[]>> {
    return this.get('/v1/forms', { params: { clientId: clientId, ...filter } });
  }

  public static getUserFormStats(formTypes: (typeof FormType)[keyof typeof FormType][]): Promise<ApiResponse<ClientFormUserStats>> {
    return this.get(`/v1/forms/user-stats`, { params: { formTypes } });
  }

  public static getFormStats(
    clientId: string,
    formTypes: (typeof FormType)[keyof typeof FormType][],
  ): Promise<ApiResponse<Record<string, ClientFormUserStats>>> {
    return this.get('/v1/forms/stats', { params: { clientId, formTypes } });
  }

  public static getFormsDataWarehousePaged(
    clientId: string,
    filter: ActivityPagedFilter = {},
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<FormListItem[]>> {
    return this.post('/v1/forms/paged-dw', { ClientId: clientId, ...filter }, config);
  }

  public static getForm(formId: string): Promise<ApiResponse<ClientForm | DocumentResponse>> {
    return this.get<ClientForm>(`/v1/forms/${formId}`);
  }

  public static getFormProgress(formId: string): Promise<ApiResponse<ClientFormProgressResponse>> {
    return this.get(`/v1/forms/${formId}/progress`);
  }

  public static forceProgressRecalculation(formId: string): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/forms/${formId}/force-progress-recalculation`);
  }

  public static deleteForm(formId: string): Promise<ApiResponse<boolean>> {
    return this.delete(`/v1/forms/${formId}`);
  }

  public static deleteFormBulk(formIds: string[]): Promise<ApiResponse<boolean>> {
    return this.post(`/v1/forms/bulk-delete`, formIds);
  }

  public static archiveForm(formId: string): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/forms/${formId}/archive`);
  }

  public static restoreForm(formId: string): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/forms/${formId}/unarchive`);
  }

  public static getFormInfo(formId: string): Promise<ApiResponse<ClientFormMetaDataResponse>> {
    return this.get(`/v1/forms/${formId}/info`);
  }

  public static updateForm(
    formId: string,
    accessType: Access,
    dueDateUtc: Date | null,
    effectiveDateUtc: Date | null,
    subtitle: string,
    clientModuleId: string,
    clientModuleSectionId: string,
  ): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/forms/${formId}`, {
      dueDateUtc: dueDateUtc,
      subtitle: subtitle,
      clientModuleId: clientModuleId,
      clientModuleSectionId: clientModuleSectionId,
      accessType: accessType,
      effectiveDateUtc,
    });
  }

  public static completeForm(formId: string, remark: string | null = null): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/forms/${formId}/complete`, { remark });
  }

  public static enforceEffectiveness(formId: string, reason: string): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/forms/${formId}/enforce-effectiveness`, { reason });
  }

  public static updateEffectiveDate(formId: string, effectiveDateUtc: Date | null, reason: string): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/forms/${formId}/effective-date`, { effectiveDateUtc, reason });
  }

  public static submitForm(formId: string, remark: string | null = null): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/forms/${formId}/submit`, { remark });
  }

  public static approveForm(formId: string, remark: string | null = null, signatureDetails?: SignatureDetails): Promise<ApiResponse<SignResponse>> {
    return this.put(`/v1/forms/${formId}/approve`, { remark, ...(signatureDetails || {}) });
  }

  public static validateForm(formId: string, remark: string | null = null, signatureDetails?: SignatureDetails): Promise<ApiResponse<SignResponse>> {
    return this.put(`/v1/forms/${formId}/validate`, { remark, ...(signatureDetails || {}) });
  }

  public static rejectForm(formId: string, remark: string | null = null): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/forms/${formId}/reject`, { remark });
  }

  public static reopenForm(
    formId: string,
    remark: string | null = null,
    dueDateUtc: Date | null = null,
    effectiveDateUtc: Date | null = null,
  ): Promise<ApiResponse<string>> {
    return this.put(`/v1/forms/${formId}/reopen`, { remark, dueDateUtc, effectiveDateUtc });
  }

  public static getReferences(formId: string, direction: FormReferenceDirection, masterOnly?: boolean): Promise<ApiResponse<AssociationListItem[]>> {
    return this.get(`/v1/forms/${formId}/references`, { params: { direction, masterOnly } });
  }

  public static getActionForms(
    formId: string,
    actionId: string,
    pageNumber = 1,
    pageSize = 15,
    templateId?: string,
    search?: string,
  ): Promise<ApiResponse<{ id: string; subtitle: string }[]>> {
    return this.post(`/v1/forms/options`, { clientFormId: formId, actionId, pageNumber, pageSize, templateFormId: templateId, search });
  }

  public static createReference(formId: string, targetIds: string[], isFormAnswer: boolean): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/forms/${formId}/references`, { targetIds: targetIds, isFormAnswer });
  }

  public static removeReference(formId: string, targetFormId: string): Promise<ApiResponse<boolean>> {
    return this.delete(`/v1/forms/${formId}/references/${targetFormId}`);
  }

  public static getTemplateReferences(formId: string): Promise<ApiResponse<TemplateAssociation[]>> {
    return this.get(`/v1/forms/${formId}/template-references`);
  }

  public static ignoreOutDatedTemplates(formId: string): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/forms/${formId}/template-references/ignore-warning`);
  }

  public static updateRisk(formId: string, riskScore: number, riskQuestions: number): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/forms/${formId}/risk`, { formRisk: riskScore, formRiskQuestions: riskQuestions });
  }

  public static changeSectionStatus(formId: string, sectionId: string, status: ClientFormSectionStatus): Promise<ApiResponse<ClientFormSectionInfo>> {
    return this.post(`/v1/forms/${formId}/sections/${sectionId}/status`, { status });
  }

  //#region Users

  public static getFormUsers(formId: string, ignoredErrorStatusCodes?: number[]): Promise<ApiResponse<ClientFormUserDto[]>> {
    return this.get(`/v1/forms/${formId}/users`, { ignoredErrorStatusCodes });
  }

  public static getFormAccess(formId: string, ignoredErrorStatusCodes?: number[]): Promise<ApiResponse<FormAccessResponse>> {
    return this.get(`/v1/forms/access/${formId}`, { ignoredErrorStatusCodes });
  }

  public static getFormAccessSnapshot(formId: string): Promise<ApiResponse<FormAccessSnapshotResponse>> {
    return this.get(`/v1/forms/access/${formId}/snapshot`);
  }

  public static addOrUpdateFormUsers(formId: string, dto: ClientFormUserDto[], sectionId?: string | null): Promise<ApiResponse<ClientFormUser[]>> {
    return this.post(`/v1/forms/${formId}/users${sectionId ? '/sections/' + sectionId : ''}`, dto);
  }

  /** Each role group is posted to the endpoint for its own scope — `parentResourceId` set means that
   *  section's access, null means the whole form — so one call can write several scopes at once (a rule
   *  group re-scoped to a section from the form-level editor). Every post fully replaces its scope's
   *  policies for that role.
   *
   *  There is no transactional batch write, so the groups are applied strictly in the given order, stopping
   *  at the first failure: callers order them so a scope gains an assignment before the scope it moved off
   *  gives it up (see `memberAssignmentEntriesToRoleGroupRequests`), which keeps a part-applied save
   *  recoverable — a retry re-posts every scope, each write being a full replace. */
  public static async addOrUpdateFormAccess(formId: string, dto: FormAccessRoleGroupRequest[]): Promise<ApiResponse<ClientFormUser[]>[]> {
    const responses: ApiResponse<ClientFormUser[]>[] = [];
    for (const roleGroup of dto) {
      responses.push(
        await this.post<ClientFormUser[]>(
          `/v1/forms/access/${formId}${roleGroup.parentResourceId ? '/section/' + roleGroup.parentResourceId : ''}`,
          roleGroup,
        ),
      );
    }
    return responses;
  }

  public static removeFormUser(formId: string, userId: string): Promise<ApiResponse<ClientFormUser[]>> {
    return this.delete(`/v1/forms/${formId}/users`, { params: { userId: userId } });
  }

  public static getCurrentFormUser(formUsers: ClientFormUser[], currentUser: User, sectionId: string | null = null): ClientFormUser | null {
    const updatedFormUsers = formUsers.map((formUser) => {
      // only add other roles if section id is not provided
      if (!sectionId) {
        const otherRoles = formUsers
          .filter((user) => user.id === formUser.id && user.role !== formUser.role)
          .map((user) => ({ role: user.role, requiresAction: user.requiresAction }));
        return { ...formUser, otherRoles };
      }
      return formUser;
    });
    return updatedFormUsers.find((user) => user.id === currentUser.id && (user.formSectionId || null) === sectionId) || null;
  }

  //#endregion

  public static updateVisibility(formId: string, isVisible: boolean): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/forms/${formId}/visibility`, JSON.stringify(isVisible), { headers: { 'Content-Type': 'application/json' } });
  }

  //#region Shares

  public static getFormShares(formId: string): Promise<ApiResponse<ClientFormShare[]>> {
    return this.get(`/v1/forms/${formId}/shares`);
  }

  public static updateFormShares(formId: string, clientIds: string[]): Promise<ApiResponse<boolean>> {
    return this.post(`/v1/forms/${formId}/shares`, { clientIds: clientIds });
  }

  //#endregion

  public static getUserHistories(formId: string): Promise<ApiResponse<ClientFormVersionHistory[]>> {
    return this.get(`/v1/forms/${formId}/users/history`);
  }

  public static getTasksPaged(clientId: string, filter: ActivityPagedFilter, config?: AxiosRequestConfig): Promise<ApiResponse<FormListItem[]>> {
    return this.get('/v1/users/tasks-paged', { params: { clientId: clientId, ...filter }, ...config });
  }

  public static getAttachments(clientFormId: string): Promise<ApiResponse<Attachment[]>> {
    return this.get(`/v1/forms/${clientFormId}/files`);
  }

  public static addAttachment(clientFormid: string, fileId: string, name: string): Promise<ApiResponse<boolean>> {
    return this.post(`/v1/forms/${clientFormid}/files/${fileId}`, { friendlyName: name });
  }

  public static deleteAttachment(clientFormId: string, fileId: string): Promise<ApiResponse<boolean>> {
    return this.delete(`/v1/forms/${clientFormId}/files/${fileId}`);
  }

  public static updateAttachmentOrder(clientFormId: string, fileIds: string[]): Promise<ApiResponse<boolean>> {
    return this.put(`/v1/forms/${clientFormId}/files/order`, fileIds);
  }

  public static getDocumentTraineeRecords(clientFormId: string): Promise<ApiResponse<DocumentTrainingEventUserRecord[]>> {
    return this.get(`/v1/forms/${clientFormId}/trainee-records`);
  }

  public static getFormVersionStatuses(payload: {
    clientFormIds: string[];
    statuses: VersionState[];
  }): Promise<ApiResponse<Record<string, Record<string, VersionState>>>> {
    return this.post(`/v1/forms/version-status`, payload);
  }

  public static getFormId(formId: string, version: number): Promise<ApiResponse<string>> {
    return this.get(`/v1/forms/${formId}/version/${version}`);
  }

  public static hasTrainingEvents(clientFormId: string): Promise<ApiResponse<boolean>> {
    return this.get(`/v1/forms/${clientFormId}/has-training-events`);
  }

  public static getBaseIds(formIds: string[]): Promise<ApiResponse<Record<string, string>>> {
    return this.get(`/v1/forms/base-ids`, { params: { formIds } });
  }

  public static async getSignedFile(clientFormId: string): Promise<Blob> {
    const res = await this.get<ArrayBuffer>(`/v1/forms/${clientFormId}/signed-file`, { responseType: 'arraybuffer' });

    if (res.status !== 200) {
      // eslint-disable-next-line no-throw-literal
      throw { ...res, message: `Could not load signed file for ${clientFormId}.` };
    }

    return new Blob([res.data], { type: res.headers['content-type']?.toString() });
  }
}
